import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {type BulletItem, QualityItem} from '@Components/download-panel/components/quality-item/quality-item';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {
  CHEAP_PRODUCT_ID,
  EXPENSIVE_PRODUCT_ID,
  FREEBIE_PRODUCT_ID,
  MAX_FREEBIE_DIMENSION,
  MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT,
  getDisplayPriceForProduct,
} from '@Libraries/poster-download-library';
import {TagType} from '@Components/tag';
import styles from './download-image-quality-option.module.scss';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import {getWidthAndHeightInPixels} from '@Libraries/poster-object.library';

export function DownloadImageQualityOption(): ReactElement {
  const {
    isWebDownload,
    posterObject,
    selectedPages,
    setSelectedDisplaySizeOption,
    needsPurchase,
    setScale,
    scale,
    selectedProductId,
    setSelectedProductId,
    productsData,
    appleDownloadProducts,
  } = useDownloadPanelData();
  const CHEAP_PRODUCT = productsData?.find((productsData) => productsData.id === String(CHEAP_PRODUCT_ID));
  const EXPENSIVE_PRODUCT = productsData?.find((productsData) => productsData.id === String(EXPENSIVE_PRODUCT_ID));

  const areDimensionsInFreeRange = (): boolean => {
    if (isWebDownload) {
      return scale <= 1;
    }
    const {width, height} = getWidthAndHeightInPixels(posterObject);
    return Math.max(width, height) * scale <= MAX_FREEBIE_DIMENSION;
  };

  useEffect(() => {
    if (
      ((!areDimensionsInFreeRange() || selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) && selectedProductId === FREEBIE_PRODUCT_ID) ||
      selectedProductId === EXPENSIVE_PRODUCT_ID
    ) {
      if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
        setSelectedProductId(EXPENSIVE_PRODUCT_ID);
      } else {
        setSelectedProductId(CHEAP_PRODUCT_ID);
      }
    }
  }, [scale, isWebDownload, selectedPages.length]);

  const onClickHandler = (id: number): void => {
    if (id === selectedProductId) {
      return;
    }

    setSelectedProductId(id);
  };

  const getCheapProductTagText = (): string | undefined => {
    if (needsPurchase) {
      if (selectedProductId === EXPENSIVE_PRODUCT_ID) {
        return getDisplayPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts) ?? undefined;
      }
      return getDisplayPriceForProduct(CHEAP_PRODUCT, appleDownloadProducts) ?? undefined;
    }
    return undefined;
  };

  const getBulletsForFreeProduct = (): BulletItem[] => {
    const items: BulletItem[] = [];

    if (selectedPages.length > 1) {
      items.push({
        text: window.i18next.t('pmwjs_x_page_limit', {limit: MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT + 1}),
        showSuccessBullet: false,
      });
    }

    return items;
  };

  const getBulletsForPaidProduct = (): BulletItem[] => {
    const items = [
      {
        text: window.i18next.t('pmwjs_no_watermark'),
        showSuccessBullet: true,
      },
    ];

    if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
      items.push({
        text: window.i18next.t('pmwjs_no_limits'),
        showSuccessBullet: false,
      });
    }
    return items;
  };

  return (
    <div className={`flex flex-v-row gap-2`}>
      <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_quality')} />
      <div className={`flex-h-row gap-3`}>
        <QualityItem
          id={`${FREEBIE_PRODUCT_ID}`}
          tagText={needsPurchase ? window.i18next.t('pmwjs_free') : undefined}
          onClick={(): void => {
            const {width, height} = getWidthAndHeightInPixels(posterObject);
            if (Math.max(width, height) > MAX_FREEBIE_DIMENSION && !isWebDownload) {
              const newScale = MAX_FREEBIE_DIMENSION / Math.max(width, height);
              setScale(newScale);
              setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL_COMPRESSED);
            } else {
              setScale(1);
              setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL);
            }

            onClickHandler(FREEBIE_PRODUCT_ID);
          }}
          text={window.i18next.t('pmwjs_basic_poster_download')}
          isSelected={selectedProductId === FREEBIE_PRODUCT_ID}
          bullets={getBulletsForFreeProduct()}
        />
        <QualityItem
          id={`image-paid-product`}
          tagClassName={styles.secondaryTag}
          tagType={TagType.SECONDARY_LIGHT}
          tagText={getCheapProductTagText()}
          text={window.i18next.t('pmwjs_high')}
          onClick={(): void => {
            if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
              onClickHandler(EXPENSIVE_PRODUCT_ID);
              return;
            }
            onClickHandler(CHEAP_PRODUCT_ID);
          }}
          bullets={isWebDownload ? [] : getBulletsForPaidProduct()}
          isSelected={selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT ? selectedProductId === EXPENSIVE_PRODUCT_ID : selectedProductId === CHEAP_PRODUCT_ID}
        />
      </div>
    </div>
  );
}
